import { twMerge } from 'tailwind-merge';

/**
 * Mode for repository target selection inside `TargetSelector`.
 *
 * - `'search'`: pick existing Repository assets via search/dropdown.
 * - `'upload'`: upload a zip that the backend turns into a file-type
 *   Repository asset.
 */
export type RepositoryMode = 'search' | 'upload';

export interface RepositoryModeToggleProps {
  mode: RepositoryMode;
  onModeChange: (next: RepositoryMode) => void;
  className?: string;
}

interface PillOption {
  value: RepositoryMode;
  label: string;
  testId: string;
}

const OPTIONS: PillOption[] = [
  { value: 'search', label: 'Search Repositories', testId: 'repo-mode-search' },
  { value: 'upload', label: 'Upload Zip', testId: 'repo-mode-upload' },
];

/**
 * RepositoryModeToggle - Two side-by-side outlined buttons that toggle between
 * "Search Repositories" and "Upload Zip" inside TargetSelector's repository
 * mode.
 *
 * - `<div role="radiogroup">` wrapper with descriptive aria-label
 * - Each option is its own outlined `<button type="button" role="radio">` with
 *   `aria-checked`; active = filled primary, inactive = outlined
 * - Tab/Enter/Space accessible via real `<button>` elements
 */
export function RepositoryModeToggle({
  mode,
  onModeChange,
  className,
}: RepositoryModeToggleProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Repository selection mode"
      data-testid="repo-mode-toggle"
      className={twMerge('flex items-stretch gap-2', className)}
    >
      {OPTIONS.map(option => {
        const isActive = mode === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isActive}
            data-testid={option.testId}
            onClick={() => {
              if (!isActive) {
                onModeChange(option.value);
              }
            }}
            className={twMerge(
              'focus-visible:ring-chariot-primary rounded-md border px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2',
              isActive
                ? 'border-chariot-primary bg-chariot-primary text-white'
                : 'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary hover:border-chariot-primary/60 hover:text-chariot-text-primary'
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
